import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClientError, type TypedDocumentString } from 'graphql-request';
import { GraphQLRequestError } from '~/lib/GraphQLRequestError.ts';
import { graphqlRequest } from '~/lib/graphql-client';

async function runGraphqlMutation<T, V>(
  mutation: TypedDocumentString<T, V>,
  variables: V,
  execute?: (variables: V) => Promise<T>
): Promise<T> {
  try {
    return await (execute
      ? execute(variables)
      : graphqlRequest(mutation, variables as object));
  } catch (error) {
    if (error instanceof ClientError && error.response.errors) {
      throw new GraphQLRequestError(error.response.errors);
    }
    throw error;
  }
}

type GraphqlMutationOptions<T, V> = Readonly<{
  invalidateOnError?: boolean;
  shouldInvalidate?: (data: T, variables: V) => boolean;
}>;

export function useGraphqlMutation<T, V = object>(
  mutation: TypedDocumentString<T, V>,
  invalidateQueries?: string[],
  execute?: (variables: V) => Promise<T>,
  {
    invalidateOnError = false,
    shouldInvalidate = () => true,
  }: GraphqlMutationOptions<T, V> = {}
) {
  const queryClient = useQueryClient();

  const refetch = async () => {
    if (!invalidateQueries) {
      return;
    }
    // A same-keyed fetch already in flight from another mutation's invalidation would
    // otherwise absorb this one, so its (possibly pre-write) result gets reused instead
    // of a fresh request — cancelling it first forces invalidate to issue a real refetch.
    await Promise.all(
      invalidateQueries.map(async (queryKey) => {
        await queryClient.cancelQueries({ queryKey: [queryKey] });
        await queryClient.invalidateQueries({ queryKey: [queryKey] });
      })
    );
  };

  return useMutation({
    mutationFn: (variables: V) =>
      runGraphqlMutation(mutation, variables, execute),
    onSuccess: async (data, variables) => {
      if (shouldInvalidate(data, variables)) {
        await refetch();
      }
    },
    onError: (error) => {
      console.error('GraphQL mutation error:', error);
      if (invalidateOnError) {
        void refetch();
      }
    },
  });
}
